import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  CheckCircle2, Circle, Plus, Trash2, Calendar, 
  MessageSquare, ClipboardCheck, CalendarClock, RefreshCw, 
  Inbox, AlertTriangle, Clock, Pencil, Send, MessageCircle, Loader2,
  Users, X, UserPlus, FolderOpen, Folder, ChevronDown, ChevronRight,
  Archive, MoreHorizontal, FolderPlus, Timer, Settings, GripVertical,
  CheckSquare, Square
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;

// Project color options
const PROJECT_COLORS = [
  { id: 'blue', name: 'Blue', value: '#3B82F6' },
  { id: 'green', name: 'Green', value: '#22C55E' },
  { id: 'purple', name: 'Purple', value: '#A855F7' },
  { id: 'orange', name: 'Orange', value: '#F97316' },
  { id: 'red', name: 'Red', value: '#EF4444' },
  { id: 'yellow', name: 'Yellow', value: '#EAB308' },
  { id: 'pink', name: 'Pink', value: '#EC4899' },
  { id: 'cyan', name: 'Cyan', value: '#06B6D4' },
  { id: 'indigo', name: 'Indigo', value: '#6366F1' },
  { id: 'gray', name: 'Gray', value: '#6B7280' },
];

const getProjectColor = (colorId) => {
  return PROJECT_COLORS.find(c => c.id === colorId)?.value || '#6B7280';
};

// Estimated time options
const TIME_ESTIMATES = [
  { value: '15m', label: '15 min' },
  { value: '30m', label: '30 min' },
  { value: '1h', label: '1 hour' },
  { value: '2h', label: '2 hours' },
  { value: '4h', label: '4 hours' },
  { value: '1d', label: '1 day' },
  { value: '2d', label: '2 days' },
  { value: '1w', label: '1 week' },
];

// Category definitions with keywords and styling
const CATEGORIES = {
  followups: {
    name: 'Follow-ups',
    icon: RefreshCw,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    bgColor: 'bg-blue-500/5',
    keywords: ['follow up', 'follow-up', 'followup', 'check in', 'check-in', 'checkin', 'touch base', 'reach out']
  },
  approvals: {
    name: 'Approvals',
    icon: ClipboardCheck,
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    bgColor: 'bg-purple-500/5',
    keywords: ['approve', 'approval', 'review', 'sign', 'signature', 'confirm', 'verify', 'authorize']
  },
  scheduling: {
    name: 'Scheduling',
    icon: CalendarClock,
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    bgColor: 'bg-orange-500/5',
    keywords: ['schedule', 'meeting', 'call', 'appointment', 'calendar', 'book', 'reschedule', 'demo']
  },
  communications: {
    name: 'Communications',
    icon: MessageSquare,
    color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    bgColor: 'bg-cyan-500/5',
    keywords: ['email', 'message', 'text', 'dm', 'respond', 'reply', 'contact', 'send', 'urgent message']
  },
  general: {
    name: 'General',
    icon: Inbox,
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    bgColor: 'bg-gray-500/5',
    keywords: []
  }
};

// Categorize a task based on its title and description
const categorizeTask = (task) => {
  const text = `${task.title || ''} ${task.description || ''}`.toLowerCase();
  
  for (const [key, category] of Object.entries(CATEGORIES)) {
    if (key === 'general') continue;
    if (category.keywords.some(kw => text.includes(kw))) {
      return key;
    }
  }
  return 'general';
};

// Check if task is overdue
const isOverdue = (task) => {
  if (!task.due_date || task.status === 'completed') return false;
  const dueDate = new Date(task.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

// Check if task is due today
const isDueToday = (task) => {
  if (!task.due_date || task.status === 'completed') return false;
  const dueDate = new Date(task.due_date);
  const today = new Date();
  return dueDate.toDateString() === today.toDateString();
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Recursive component for rendering sub-projects with nested children
const SubProjectMenuItem = ({ child, onSelect, getProjectColor, onEdit, onArchive, onDelete }) => {
  const hasChildren = child.children && child.children.length > 0;
  
  if (hasChildren) {
    // If this sub-project has its own children, render as a sub-menu
    return (
      <DropdownMenuSub key={child.id}>
        <DropdownMenuSubTrigger className="flex items-center">
          <div 
            className="w-3 h-3 rounded-sm mr-2 flex-shrink-0" 
            style={{ backgroundColor: getProjectColor(child.color) }}
          />
          <span className="flex-1">{child.name}</span>
          {child.progress !== undefined && (
            <span className="text-xs opacity-70 ml-1">({child.progress}%)</span>
          )}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem onClick={() => onSelect(child.id)}>
            <FolderOpen className="h-4 w-4 mr-2" />
            View Tasks
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onEdit(child)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Sub-project
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onArchive(child.id)} className="text-orange-500">
            <Archive className="h-4 w-4 mr-2" />
            Archive
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(child.id)} className="text-red-500">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Sub-projects</div>
          {child.children.map(grandchild => (
            <SubProjectMenuItem 
              key={grandchild.id}
              child={grandchild} 
              onSelect={onSelect}
              getProjectColor={getProjectColor}
              onEdit={onEdit}
              onArchive={onArchive}
              onDelete={onDelete}
            />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }
  
  // Sub-project without children - render as a sub-menu with edit option
  return (
    <DropdownMenuSub key={child.id}>
      <DropdownMenuSubTrigger className="flex items-center">
        <div 
          className="w-3 h-3 rounded-sm mr-2 flex-shrink-0" 
          style={{ backgroundColor: getProjectColor(child.color) }}
        />
        <span className="flex-1">{child.name}</span>
        {child.progress !== undefined && (
          <span className="text-xs opacity-70 ml-1">({child.progress}%)</span>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem onClick={() => onSelect(child.id)}>
          <FolderOpen className="h-4 w-4 mr-2" />
          View Tasks
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(child)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit Sub-project
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onArchive(child.id)} className="text-orange-500">
          <Archive className="h-4 w-4 mr-2" />
          Archive
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(child.id)} className="text-red-500">
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

// Sortable Project Button Component for drag-and-drop reordering
const SortableProjectButton = ({ project, selectedProject, setSelectedProject, getProjectColor, openEditProject, archiveProject, deleteProject, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-0.5">
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 -mr-1"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3 w-3" />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={selectedProject === project.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedProject(project.id)}
            className={`h-8 gap-1 ${isDragging ? 'shadow-lg' : ''}`}
          >
            <div 
              className="w-3 h-3 rounded-sm flex-shrink-0" 
              style={{ backgroundColor: getProjectColor(project.color) }}
            />
            <span className="max-w-[100px] truncate">{project.name}</span>
            {project.progress !== undefined && (
              <span className="text-xs opacity-70">({project.progress}%)</span>
            )}
            {project.children && project.children.length > 0 && (
              <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
            )}
          </Button>
        </DropdownMenuTrigger>
        {children}
      </DropdownMenu>
    </div>
  );
};

// Sortable Task Card Component for drag-and-drop
const SortableTaskCard = ({ task, openEditDialog, updateTaskStatus, deleteTask, flatProjects, getProjectColor, priorityColors, CATEGORIES, formatDate, isBulkMode, isSelected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const category = CATEGORIES[task.category];
  const CategoryIcon = category?.icon || Inbox;
  const commentCount = task.comments?.length || 0;
  const assigneeCount = task.assignees?.length || 0;
  const taskProject = flatProjects.find(p => p.id === task.project_id);
  const timeEstimate = task.estimated_time;

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        className={`transition-all cursor-pointer hover:shadow-md hover:border-primary/50 ${task.status === 'completed' ? 'opacity-60' : ''} ${task.isOverdue ? 'border-red-500/50' : ''} ${isDragging ? 'shadow-lg' : ''} ${isSelected ? 'ring-2 ring-primary' : ''}`}
        data-testid={`task-${task.id}`}
      >
        <CardContent className="flex items-start gap-2 p-4">
          {/* Bulk Selection Checkbox */}
          {isBulkMode && (
            <button
              onClick={(e) => onToggleSelect(task.id, e)}
              className="mt-1 flex-shrink-0 text-muted-foreground hover:text-primary"
              data-testid={`select-task-${task.id}`}
            >
              {isSelected ? (
                <CheckSquare className="h-5 w-5 text-primary" />
              ) : (
                <Square className="h-5 w-5" />
              )}
            </button>
          )}
          
          {/* Drag Handle */}
          {!isBulkMode && (
            <button
              {...attributes}
              {...listeners}
              className="mt-1 flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 -ml-1"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed', e); }}
            className="mt-1 flex-shrink-0"
            data-testid={`toggle-task-${task.id}`}
          >
            {task.status === 'completed' ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground hover:text-primary" />
            )}
          </button>
          
          <div className="flex-1 min-w-0" onClick={() => openEditDialog(task)}>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                {task.title}
              </span>
              <Badge variant="outline" className={priorityColors[task.priority]}>
                {task.priority}
              </Badge>
              <Badge variant="outline" className={category?.color}>
                <CategoryIcon className="h-3 w-3 mr-1" />
                {category?.name}
              </Badge>
              {taskProject && (
                <Badge 
                  variant="outline" 
                  className="gap-1"
                  style={{ 
                    borderColor: `${getProjectColor(taskProject.color)}40`,
                    backgroundColor: `${getProjectColor(taskProject.color)}10`,
                    color: getProjectColor(taskProject.color)
                  }}
                >
                  <Folder className="h-3 w-3" />
                  {taskProject.name}
                </Badge>
              )}
              {timeEstimate && (
                <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600 border-cyan-500/20">
                  <Timer className="h-3 w-3 mr-1" />
                  {timeEstimate}
                </Badge>
              )}
              {task.tags?.includes('prime-created') && (
                <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/20">
                  🔱 Prime
                </Badge>
              )}
              {assigneeCount > 0 && (
                <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                  <Users className="h-3 w-3 mr-1" />
                  {assigneeCount}
                </Badge>
              )}
              {commentCount > 0 && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                  <MessageCircle className="h-3 w-3 mr-1" />
                  {commentCount}
                </Badge>
              )}
              {task.isOverdue && (
                <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
              {task.isDueToday && !task.isOverdue && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                  <Clock className="h-3 w-3 mr-1" />
                  Due Today
                </Badge>
              )}
            </div>
            {/* Assignee Avatars */}
            {assigneeCount > 0 && (
              <div className="flex items-center gap-1 mt-2">
                <div className="flex -space-x-2">
                  {task.assignees.slice(0, 3).map((assignee, idx) => (
                    <Avatar key={assignee.id} className="h-6 w-6 border-2 border-background">
                      <AvatarFallback className="text-[10px] bg-primary/20">
                        {assignee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                {assigneeCount > 3 && (
                  <span className="text-xs text-muted-foreground ml-1">+{assigneeCount - 3} more</span>
                )}
              </div>
            )}
            {task.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{task.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              {task.due_date && (
                <span className={`flex items-center gap-1 ${task.isOverdue ? 'text-red-500' : ''}`}>
                  <Calendar className="h-3 w-3" />
                  Due: {formatDate(task.due_date)}
                </span>
              )}
              {task.created_by_name && (
                <span>Created by: {task.created_by_name}</span>
              )}
              <span>{formatDate(task.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); openEditDialog(task); }}
              className="text-muted-foreground hover:text-primary"
              data-testid={`edit-task-${task.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); deleteTask(task.id, e); }}
              className="text-muted-foreground hover:text-destructive"
              data-testid={`delete-task-${task.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', due_date: '', project_id: '', estimated_time: '' });
  
  // Bulk selection state
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [isBulkMode, setIsBulkMode] = useState(false);
  
  // Projects state
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all'); // 'all', 'inbox', or project_id
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', color: 'blue', parent_id: '' });
  const [expandedProjects, setExpandedProjects] = useState({});
  
  // Edit project state
  const [editingProject, setEditingProject] = useState(null);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({ name: '', description: '', color: 'blue' });
  
  // Edit task state
  const [editingTask, setEditingTask] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', status: 'pending', project_id: '', estimated_time: '' });
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  
  // Users/Assignees state
  const [allUsers, setAllUsers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Bulk action handlers
  const toggleTaskSelection = (taskId, e) => {
    e?.stopPropagation();
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const selectAllTasks = () => {
    if (selectedTasks.size === filteredTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasks.map(t => t.id)));
    }
  };

  const clearSelection = () => {
    setSelectedTasks(new Set());
    setIsBulkMode(false);
  };

  const bulkMoveToProject = async (projectId) => {
    if (selectedTasks.size === 0) return;
    
    try {
      const promises = Array.from(selectedTasks).map(taskId =>
        axios.post(`${API}/api/tasks/${taskId}/move-to-project`, { project_id: projectId || null })
      );
      await Promise.all(promises);
      toast.success(`${selectedTasks.size} task(s) moved successfully`);
      clearSelection();
      fetchTasks();
    } catch (error) {
      console.error('Failed to move tasks:', error);
      toast.error('Failed to move some tasks');
    }
  };

  const bulkDeleteTasks = async () => {
    if (selectedTasks.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedTasks.size} task(s)?`)) return;
    
    try {
      const promises = Array.from(selectedTasks).map(taskId =>
        axios.delete(`${API}/api/tasks/${taskId}`)
      );
      await Promise.all(promises);
      toast.success(`${selectedTasks.size} task(s) deleted`);
      clearSelection();
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete tasks:', error);
      toast.error('Failed to delete some tasks');
    }
  };

  const bulkMarkComplete = async () => {
    if (selectedTasks.size === 0) return;
    
    try {
      const promises = Array.from(selectedTasks).map(taskId =>
        axios.patch(`${API}/api/tasks/${taskId}`, { status: 'completed' })
      );
      await Promise.all(promises);
      toast.success(`${selectedTasks.size} task(s) marked complete`);
      clearSelection();
      fetchTasks();
    } catch (error) {
      console.error('Failed to update tasks:', error);
      toast.error('Failed to update some tasks');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchProjects();
  }, [user]);

  // Re-fetch tasks when project filter changes
  useEffect(() => {
    if (user?.id) {
      fetchTasks();
    }
  }, [selectedProject]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder tasks
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = tasks.findIndex(t => t.id === active.id);
      const newIndex = tasks.findIndex(t => t.id === over.id);

      const newTasks = arrayMove(tasks, oldIndex, newIndex);
      setTasks(newTasks);

      // Update sort_order for all affected tasks
      const taskOrders = newTasks.map((task, index) => ({
        id: task.id,
        sort_order: index
      }));

      try {
        await axios.post(`${API}/api/tasks/reorder`, { task_orders: taskOrders });
        toast.success('Tasks reordered');
      } catch (error) {
        console.error('Failed to reorder tasks:', error);
        toast.error('Failed to save task order');
        // Revert on error
        fetchTasks();
      }
    }
  };

  // Handle drag end - reorder projects
  const handleProjectDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = projects.findIndex(p => p.id === active.id);
      const newIndex = projects.findIndex(p => p.id === over.id);

      const newProjects = arrayMove(projects, oldIndex, newIndex);
      setProjects(newProjects);

      // Update sort_order for all projects
      const projectOrders = newProjects.map((project, index) => ({
        id: project.id,
        sort_order: index
      }));

      try {
        await axios.post(`${API}/api/projects/reorder`, { project_orders: projectOrders });
        toast.success('Projects reordered');
      } catch (error) {
        console.error('Failed to reorder projects:', error);
        toast.error('Failed to save project order');
        // Revert on error
        fetchProjects();
      }
    }
  };

  const fetchTasks = async () => {
    if (!user?.id) return;
    try {
      let url = `${API}/api/tasks?user_id=${user.id}`;
      if (selectedProject && selectedProject !== 'all') {
        url += `&project_id=${selectedProject}`;
      }
      const response = await axios.get(url);
      setTasks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/api/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  // Fetch all users for assignee picker
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/users`);
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Fetch comments for a task
  const fetchComments = async (taskId) => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/api/tasks/${taskId}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Add a comment
  const addComment = async () => {
    if (!newComment.trim() || !editingTask?.id) return;
    setAddingComment(true);
    try {
      const response = await axios.post(`${API}/api/tasks/${editingTask.id}/comments`, {
        content: newComment.trim()
      });
      setComments(response.data.comments || []);
      setNewComment('');
      
      // Update task in list with new comment count
      setTasks(tasks.map(t => 
        t.id === editingTask.id 
          ? { ...t, comments: response.data.comments }
          : t
      ));
      
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Delete a comment
  const deleteComment = async (commentId) => {
    if (!editingTask?.id) return;
    try {
      await axios.delete(`${API}/api/tasks/${editingTask.id}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      
      // Update task in list
      setTasks(tasks.map(t => 
        t.id === editingTask.id 
          ? { ...t, comments: (t.comments || []).filter(c => c.id !== commentId) }
          : t
      ));
      
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Compute categorized tasks and stats
  const { categorizedTasks, stats, categoryStats } = useMemo(() => {
    const categorized = tasks.map(task => ({
      ...task,
      category: categorizeTask(task),
      isOverdue: isOverdue(task),
      isDueToday: isDueToday(task)
    }));

    const pending = categorized.filter(t => t.status === 'pending');
    const completed = categorized.filter(t => t.status === 'completed');
    const overdue = pending.filter(t => t.isOverdue);
    const dueToday = pending.filter(t => t.isDueToday);

    const catStats = {};
    Object.keys(CATEGORIES).forEach(key => {
      catStats[key] = pending.filter(t => t.category === key).length;
    });

    return {
      categorizedTasks: categorized,
      stats: {
        total: tasks.length,
        pending: pending.length,
        completed: completed.length,
        overdue: overdue.length,
        dueToday: dueToday.length
      },
      categoryStats: catStats
    };
  }, [tasks]);

  // Get project path/breadcrumb for a project ID
  const getProjectPath = (projectId) => {
    if (!projectId || projectId === 'all' || projectId === 'inbox') return null;
    
    const findPath = (projects, targetId, path = []) => {
      for (const project of projects) {
        const newPath = [...path, project];
        if (project.id === targetId) {
          return newPath;
        }
        if (project.children && project.children.length > 0) {
          const found = findPath(project.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findPath(projects, projectId);
  };

  // Get current project path for display
  const currentProjectPath = useMemo(() => {
    return getProjectPath(selectedProject);
  }, [selectedProject, projects]);

  const createTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    try {
      const taskData = {
        ...newTask,
        assigned_to: user.id,
        created_by: user.id,
        created_by_name: user.name,
        project_id: newTask.project_id || null,
        estimated_time: newTask.estimated_time || null
      };
      const response = await axios.post(`${API}/api/tasks`, taskData);
      setTasks([response.data, ...tasks]);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', project_id: '', estimated_time: '' });
      setIsAddOpen(false);
      toast.success('Task created!');
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  // Create a new project
  const createProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    try {
      const response = await axios.post(`${API}/api/projects`, {
        ...newProject,
        parent_id: newProject.parent_id || null
      });
      await fetchProjects();
      setNewProject({ name: '', description: '', color: 'blue', parent_id: '' });
      setIsProjectOpen(false);
      toast.success('Project created!');
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  // Archive a project
  const archiveProject = async (projectId) => {
    try {
      await axios.post(`${API}/api/projects/${projectId}/archive`);
      await fetchProjects();
      if (selectedProject === projectId) {
        setSelectedProject('all');
      }
      toast.success('Project archived');
    } catch (error) {
      console.error('Failed to archive project:', error);
      toast.error('Failed to archive project');
    }
  };

  // Delete a project (with confirmation)
  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? Tasks will be unassigned but not deleted.')) {
      return;
    }
    try {
      await axios.delete(`${API}/api/projects/${projectId}`);
      await fetchProjects();
      if (selectedProject === projectId) {
        setSelectedProject('all');
      }
      toast.success('Project deleted');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  // Open edit project dialog
  const openEditProject = (project) => {
    setEditingProject(project);
    setEditProjectForm({
      name: project.name || '',
      description: project.description || '',
      color: project.color || 'blue'
    });
    setIsEditProjectOpen(true);
  };

  // Save edited project
  const saveEditedProject = async () => {
    if (!editProjectForm.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    try {
      await axios.patch(`${API}/api/projects/${editingProject.id}`, {
        name: editProjectForm.name,
        description: editProjectForm.description,
        color: editProjectForm.color
      });
      await fetchProjects();
      setIsEditProjectOpen(false);
      setEditingProject(null);
      toast.success('Project updated!');
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    }
  };

  // Move task to a project
  const moveTaskToProject = async (taskId, projectId) => {
    try {
      await axios.post(`${API}/api/tasks/${taskId}/move-to-project`, {
        project_id: projectId || null
      });
      await fetchTasks();
      toast.success(projectId ? 'Task moved to project' : 'Task moved to Inbox');
    } catch (error) {
      console.error('Failed to move task:', error);
      toast.error('Failed to move task');
    }
  };

  // Get all projects in a flat list (including nested)
  const getAllProjectsFlat = (projectList, depth = 0) => {
    let result = [];
    for (const project of projectList) {
      result.push({ ...project, depth });
      if (project.children && project.children.length > 0) {
        result = result.concat(getAllProjectsFlat(project.children, depth + 1));
      }
    }
    return result;
  };

  const flatProjects = useMemo(() => getAllProjectsFlat(projects), [projects]);

  const updateTaskStatus = async (taskId, newStatus, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.patch(`${API}/api/tasks/${taskId}`, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task updated');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`);
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Open edit dialog with task data
  const openEditDialog = (task) => {
    setEditingTask(task);
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      status: task.status || 'pending',
      project_id: task.project_id || '',
      estimated_time: task.estimated_time || ''
    });
    setAssignees(task.assignees || []);
    setUserSearchQuery('');
    setComments(task.comments || []);
    setNewComment('');
    setIsEditOpen(true);
    
    // Fetch latest comments
    fetchComments(task.id);
  };

  // Add assignee to task
  const addAssignee = (userToAdd) => {
    if (!assignees.find(a => a.id === userToAdd.id)) {
      setAssignees([...assignees, {
        id: userToAdd.id,
        name: userToAdd.name || `${userToAdd.first_name || ''} ${userToAdd.last_name || ''}`.trim(),
        email: userToAdd.email
      }]);
    }
    setUserSearchQuery('');
  };

  // Remove assignee from task
  const removeAssignee = (userId) => {
    setAssignees(assignees.filter(a => a.id !== userId));
  };

  // Filter users for search
  const filteredUsers = allUsers.filter(u => {
    const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
    const matchesSearch = fullName.includes(userSearchQuery.toLowerCase());
    const notAlreadyAssigned = !assignees.find(a => a.id === u.id);
    return matchesSearch && notAlreadyAssigned && userSearchQuery.length > 0;
  });

  // Save edited task
  const saveEditedTask = async () => {
    if (!editForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    try {
      const updateData = {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        status: editForm.status,
        assignees: assignees,
        project_id: editForm.project_id || null,
        estimated_time: editForm.estimated_time || null
      };
      
      if (editForm.due_date) {
        updateData.due_date = editForm.due_date;
      }
      
      await axios.patch(`${API}/api/tasks/${editingTask.id}`, updateData);
      
      setTasks(tasks.map(t => t.id === editingTask.id ? { ...t, ...updateData } : t));
      
      setIsEditOpen(false);
      setEditingTask(null);
      toast.success('Task updated!');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  // Filter tasks by status and category
  const filteredTasks = categorizedTasks.filter(task => {
    if (filter === 'pending' && task.status !== 'pending') return false;
    if (filter === 'completed' && task.status !== 'completed') return false;
    if (filter === 'overdue' && !task.isOverdue) return false;
    if (categoryFilter !== 'all' && task.category !== categoryFilter) return false;
    return true;
  });

  const priorityColors = {
    high: 'bg-red-500/10 text-red-500 border-red-500/20',
    medium: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    low: 'bg-green-500/10 text-green-500 border-green-500/20'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="container mx-auto p-6 max-w-5xl" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage your to-do list</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Create Project Button */}
          <Dialog open={isProjectOpen} onOpenChange={setIsProjectOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="create-project-btn">
                <FolderPlus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Name</Label>
                  <Input
                    id="project-name"
                    placeholder="Project name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project-description">Description</Label>
                  <Textarea
                    id="project-description"
                    placeholder="Description (optional)"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_COLORS.map(color => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => setNewProject({ ...newProject, color: color.id })}
                        className={`w-8 h-8 rounded-md transition-all ${newProject.color === color.id ? 'ring-2 ring-offset-2 ring-primary' : 'hover:scale-110'}`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Parent Project (optional)</Label>
                  <Select value={newProject.parent_id || 'none'} onValueChange={(v) => setNewProject({ ...newProject, parent_id: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="None (top-level project)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (top-level project)</SelectItem>
                      {flatProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          <div className="flex items-center gap-2" style={{ paddingLeft: `${project.depth * 16}px` }}>
                            <div 
                              className="w-3 h-3 rounded-sm" 
                              style={{ backgroundColor: getProjectColor(project.color) }}
                            />
                            <span>{project.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createProject} className="w-full" data-testid="create-project-submit">
                  Create Project
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Task Dialog */}
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="new-title">Title</Label>
                <Input
                  id="new-title"
                  placeholder="Task title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-description">Description</Label>
                <Textarea
                  id="new-description"
                  placeholder="Description (optional)"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Est. Time</Label>
                  <Select value={newTask.estimated_time || 'none'} onValueChange={(v) => setNewTask({ ...newTask, estimated_time: v === 'none' ? '' : v })}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue placeholder="Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {TIME_ESTIMATES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex-1">
                  <Label htmlFor="new-due-date">Due Date</Label>
                  <Input
                    id="new-due-date"
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Project</Label>
                {/* Show breadcrumb if a project with path is selected */}
                {newTask.project_id && getProjectPath(newTask.project_id) && (
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    {getProjectPath(newTask.project_id).map((p, idx) => (
                      <span key={p.id} className="flex items-center gap-1">
                        {idx > 0 && <ChevronRight className="h-3 w-3" />}
                        <span style={{ color: getProjectColor(p.color) }}>{p.name}</span>
                      </span>
                    ))}
                  </div>
                )}
                <Select value={newTask.project_id || 'inbox'} onValueChange={(v) => setNewTask({ ...newTask, project_id: v === 'inbox' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbox">
                      <div className="flex items-center gap-2">
                        <Inbox className="h-4 w-4" />
                        <span>Inbox (No Project)</span>
                      </div>
                    </SelectItem>
                    {flatProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        <div className="flex items-center gap-2" style={{ paddingLeft: `${project.depth * 16}px` }}>
                          <div 
                            className="w-3 h-3 rounded-sm" 
                            style={{ backgroundColor: getProjectColor(project.color) }}
                          />
                          <span>{project.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createTask} className="w-full" data-testid="create-task-btn">
                Create Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Edit Task Dialog with Comments */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Task
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 pr-2">
            <div className="space-y-4">
              {/* Task Details */}
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  placeholder="Task title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-task-title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Description (optional)"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={2}
                  data-testid="edit-task-description"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                    <SelectTrigger data-testid="edit-task-priority">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">🟢 Low</SelectItem>
                      <SelectItem value="medium">🟡 Medium</SelectItem>
                      <SelectItem value="high">🔴 High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="edit-task-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">⏳ Pending</SelectItem>
                      <SelectItem value="completed">✅ Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Est. Time</Label>
                  <Select value={editForm.estimated_time || 'none'} onValueChange={(v) => setEditForm({ ...editForm, estimated_time: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="edit-task-time">
                      <SelectValue placeholder="Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {TIME_ESTIMATES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-due-date">Due Date</Label>
                  <Input
                    id="edit-due-date"
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                    data-testid="edit-task-due-date"
                  />
                </div>
              </div>

              {/* Project Selection */}
              <div className="space-y-2">
                <Label>Project</Label>
                {/* Show breadcrumb if a project with path is selected */}
                {editForm.project_id && getProjectPath(editForm.project_id) && (
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    {getProjectPath(editForm.project_id).map((p, idx) => (
                      <span key={p.id} className="flex items-center gap-1">
                        {idx > 0 && <ChevronRight className="h-3 w-3" />}
                        <span style={{ color: getProjectColor(p.color) }}>{p.name}</span>
                      </span>
                    ))}
                  </div>
                )}
                <Select 
                  value={editForm.project_id || 'inbox'} 
                  onValueChange={(v) => setEditForm({ ...editForm, project_id: v === 'inbox' ? '' : v })}
                >
                  <SelectTrigger data-testid="edit-task-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbox">
                      <div className="flex items-center gap-2">
                        <Inbox className="h-4 w-4" />
                        <span>Inbox (No Project)</span>
                      </div>
                    </SelectItem>
                    {flatProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        <div className="flex items-center gap-2" style={{ paddingLeft: `${project.depth * 16}px` }}>
                          <div 
                            className="w-3 h-3 rounded-sm" 
                            style={{ backgroundColor: getProjectColor(project.color) }}
                          />
                          <span>{project.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Assignees Section */}
              <div className="py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-semibold">Assignees</Label>
                  {assignees.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {assignees.length}
                    </Badge>
                  )}
                </div>

                {/* Current Assignees */}
                {assignees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {assignees.map((assignee) => (
                      <Badge 
                        key={assignee.id} 
                        variant="secondary" 
                        className="flex items-center gap-1 py-1 px-2"
                      >
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-[10px] bg-primary/20">
                            {assignee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs">{assignee.name}</span>
                        <button
                          onClick={() => removeAssignee(assignee.id)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Add Assignee */}
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users to add..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="pl-9"
                        data-testid="assignee-search"
                      />
                    </div>
                  </div>
                  
                  {/* User Search Results */}
                  {filteredUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-40 overflow-auto">
                      {filteredUsers.slice(0, 5).map((u) => (
                        <button
                          key={u.id}
                          onClick={() => addAssignee(u)}
                          className="w-full flex items-center gap-2 p-2 hover:bg-accent text-left text-sm"
                        >
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Comments Section */}
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-semibold">Comments & Updates</Label>
                  <Badge variant="secondary" className="text-xs">
                    {comments.length}
                  </Badge>
                </div>

                {/* Comments List */}
                <div className="max-h-[150px] overflow-y-auto pr-2">
                  {loadingComments ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-30" />
                      <p>No comments yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {comments.map((comment) => (
                        <div key={comment.id} className="group flex gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                          <Avatar className="h-6 w-6 flex-shrink-0">
                            <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                              {comment.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium">{comment.author_name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeTime(comment.created_at)}
                                </span>
                                {comment.author_id === user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => deleteComment(comment.id)}
                                  >
                                    <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-foreground mt-1 whitespace-pre-wrap">{comment.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add Comment Input */}
                <div className="flex gap-2 pt-3">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && addComment()}
                    disabled={addingComment}
                    className="flex-1"
                    data-testid="new-comment-input"
                  />
                  <Button 
                    onClick={addComment} 
                    disabled={!newComment.trim() || addingComment}
                    size="icon"
                    data-testid="add-comment-btn"
                  >
                    {addingComment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Task metadata */}
              {editingTask && (
                <div className="pt-3 mt-3 border-t text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  {editingTask.created_by_name && (
                    <span>Created by: {editingTask.created_by_name}</span>
                  )}
                  {editingTask.created_at && (
                    <span>Created: {formatDate(editingTask.created_at)}</span>
                  )}
                  {editingTask.tags?.includes('prime-created') && (
                    <span className="text-purple-500">🔱 Created by Prime</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEditedTask} data-testid="save-task-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.overdue > 0 && (
          <Card 
            className="col-span-2 md:col-span-1 bg-red-500/10 border-red-500/30 cursor-pointer hover:bg-red-500/15 transition-colors"
            onClick={() => { setFilter('overdue'); setCategoryFilter('all'); }}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-500">{stats.overdue}</p>
                <p className="text-xs text-red-500/80">Overdue</p>
              </div>
            </CardContent>
          </Card>
        )}

        {stats.dueToday > 0 && (
          <Card 
            className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:bg-amber-500/15 transition-colors"
            onClick={() => { setFilter('pending'); setCategoryFilter('all'); }}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/20">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{stats.dueToday}</p>
                <p className="text-xs text-amber-500/80">Due Today</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => { setFilter('pending'); setCategoryFilter('all'); }}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/20">
              <Circle className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => { setFilter('completed'); setCategoryFilter('all'); }}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-500/20">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={categoryFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoryFilter('all')}
          className="rounded-full"
        >
          All Categories ({stats.pending})
        </Button>
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const count = categoryStats[key] || 0;
          if (count === 0 && key !== 'general') return null;
          const Icon = cat.icon;
          return (
            <Button
              key={key}
              variant={categoryFilter === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter(key)}
              className={`rounded-full ${categoryFilter === key ? '' : cat.color}`}
            >
              <Icon className="h-3 w-3 mr-1" />
              {cat.name} ({count})
            </Button>
          );
        })}
      </div>

      {/* Project Filter */}
      <div className="mb-4">
        {/* Show current project path if in a sub-project */}
        {currentProjectPath && currentProjectPath.length > 1 && (
          <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1 flex-wrap">
            <span className="font-medium">Current:</span>
            {currentProjectPath.map((p, idx) => (
              <span key={p.id} className="flex items-center gap-1">
                {idx > 0 && <ChevronRight className="h-3 w-3" />}
                <button 
                  onClick={() => setSelectedProject(p.id)}
                  className="hover:underline"
                  style={{ color: getProjectColor(p.color) }}
                >
                  {p.name}
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">Project:</span>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedProject === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedProject('all')}
              className="h-8"
            >
              <FolderOpen className="h-4 w-4 mr-1" />
              All Tasks
            </Button>
            <Button
              variant={selectedProject === 'inbox' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedProject('inbox')}
              className="h-8"
            >
              <Inbox className="h-4 w-4 mr-1" />
              Inbox
            </Button>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleProjectDragEnd}
            >
              <SortableContext
                items={projects.map(p => p.id)}
                strategy={horizontalListSortingStrategy}
              >
                {projects.map(project => (
                  <SortableProjectButton
                    key={project.id}
                    project={project}
                    selectedProject={selectedProject}
                    setSelectedProject={setSelectedProject}
                    getProjectColor={getProjectColor}
                    openEditProject={openEditProject}
                    archiveProject={archiveProject}
                    deleteProject={deleteProject}
                  >
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setSelectedProject(project.id)}>
                        <FolderOpen className="h-4 w-4 mr-2" />
                        View All Tasks
                      </DropdownMenuItem>
                      {project.children && project.children.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Sub-projects</div>
                          {project.children.map(child => (
                            <SubProjectMenuItem 
                              key={child.id}
                              child={child} 
                              onSelect={setSelectedProject}
                              getProjectColor={getProjectColor}
                              onEdit={openEditProject}
                              onArchive={archiveProject}
                              onDelete={deleteProject}
                            />
                          ))}
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => openEditProject(project)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Edit Project
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => archiveProject(project.id)}
                        className="text-orange-600"
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archive Project
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => deleteProject(project.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Project
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </SortableProjectButton>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={isEditProjectOpen} onOpenChange={setIsEditProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-project-name">Name</Label>
              <Input
                id="edit-project-name"
                placeholder="Project name"
                value={editProjectForm.name}
                onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-project-description">Description</Label>
              <Textarea
                id="edit-project-description"
                placeholder="Description (optional)"
                value={editProjectForm.description}
                onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_COLORS.map(color => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() => setEditProjectForm({ ...editProjectForm, color: color.id })}
                    className={`w-8 h-8 rounded-md transition-all ${editProjectForm.color === color.id ? 'ring-2 ring-offset-2 ring-primary' : 'hover:scale-110'}`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <Button onClick={saveEditedProject} className="w-full">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {['all', 'pending', 'completed', 'overdue'].map((f) => {
          const count = f === 'all' ? tasks.length 
            : f === 'overdue' ? stats.overdue
            : f === 'pending' ? stats.pending 
            : stats.completed;
          if (f === 'overdue' && stats.overdue === 0) return null;
          return (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={`capitalize ${f === 'overdue' ? 'text-red-500 border-red-500/30' : ''}`}
            >
              {f} ({count})
            </Button>
          );
        })}
      </div>

      {/* Bulk Action Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <Button
          variant={isBulkMode ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setIsBulkMode(!isBulkMode);
            if (isBulkMode) clearSelection();
          }}
          data-testid="toggle-bulk-mode"
        >
          <CheckSquare className="h-4 w-4 mr-2" />
          {isBulkMode ? 'Exit Selection' : 'Select Tasks'}
        </Button>
        
        {isBulkMode && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllTasks}
              data-testid="select-all-tasks"
            >
              {selectedTasks.size === filteredTasks.length ? 'Deselect All' : 'Select All'}
            </Button>
            
            {selectedTasks.size > 0 && (
              <div className="flex items-center gap-2 ml-2 pl-4 border-l">
                <span className="text-sm font-medium text-muted-foreground">
                  {selectedTasks.size} selected
                </span>
                
                {/* Move to Project dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="bulk-move-btn">
                      <FolderOpen className="h-4 w-4 mr-2" />
                      Move to Project
                      <ChevronDown className="h-3 w-3 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => bulkMoveToProject(null)}>
                      <Inbox className="h-4 w-4 mr-2" />
                      Inbox (No Project)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {flatProjects.map(project => (
                      <DropdownMenuItem 
                        key={project.id}
                        onClick={() => bulkMoveToProject(project.id)}
                      >
                        <div 
                          className="w-3 h-3 rounded-sm mr-2" 
                          style={{ backgroundColor: getProjectColor(project.color) }}
                        />
                        <span style={{ paddingLeft: `${project.depth * 12}px` }}>
                          {project.name}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={bulkMarkComplete}
                  data-testid="bulk-complete-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={bulkDeleteTasks}
                  className="text-red-500 hover:text-red-600"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Tasks List with Drag-and-Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={filteredTasks.map(t => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3" data-testid="task-list">
            {filteredTasks.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {filter === 'all' && categoryFilter === 'all' 
                      ? 'No tasks yet. Create your first task!' 
                      : `No ${filter !== 'all' ? filter : ''} ${categoryFilter !== 'all' ? CATEGORIES[categoryFilter]?.name : ''} tasks`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredTasks.map((task) => (
                <SortableTaskCard
                  key={task.id}
                  task={task}
                  openEditDialog={openEditDialog}
                  updateTaskStatus={updateTaskStatus}
                  deleteTask={deleteTask}
                  flatProjects={flatProjects}
                  getProjectColor={getProjectColor}
                  priorityColors={priorityColors}
                  CATEGORIES={CATEGORIES}
                  formatDate={formatDate}
                  isBulkMode={isBulkMode}
                  isSelected={selectedTasks.has(task.id)}
                  onToggleSelect={toggleTaskSelection}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
    </Layout>
  );
}
